<template>
  <div class="WEditorJS">
    <slot name="header" />
    <div class="editor" ref="editorDom"></div>
    <slot name="footer" />
  </div>
</template>

<script lang="ts" setup>
  // import
  import EditorJS, { type API, type OutputData } from '@editorjs/editorjs';
  import { onMounted, ref, watch, type PropType } from 'vue';
  import { EDITOR_JS_TOOLS } from './plugins';
  import _ from 'lodash';
  import type { TUploadFunction } from '@/types';

  // variables
  const editor = ref<EditorJS>();
  const editorDom = ref<HTMLElement>();

  const props = defineProps({
    placeholder: {
      type: String,
      default: 'Type something...',
    },
    defaultValue: {
      type: Object as () => OutputData,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    imageUploadFunction: {
      type: Function as PropType<TUploadFunction>,
    },
  });

  const emit = defineEmits(['updated']);

  onMounted(() => {
    // modify default config
    const tools = _.cloneDeep(EDITOR_JS_TOOLS);

    if (props.imageUploadFunction) {
      tools.image.config.uploader.uploadByFile = props.imageUploadFunction;
    }

    editor.value = new EditorJS({
      minHeight: 0,
      inlineToolbar: true,
      readOnly: props.readOnly,
      placeholder: props.placeholder, // Should be a prop
      holder: editorDom.value,
      tools,
      data: props.defaultValue,
      onChange(api: API, event: CustomEvent) {
        if (!props.readOnly) {
          editor?.value?.save().then(res => {
            emit('updated', res);
          });
        }
      },
    });
  });

  watch(
    () => props.readOnly,
    newValue => {
      editor?.value?.readOnly.toggle(newValue);
    },
  );
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/variables';
  @import '@/assets/styles/mixins';

  .WEditorJS {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
    border-radius: 12px;
    background-color: $white;

    .editor {
      width: 100%;
      padding-inline: 3rem;

      @include mobile {
        padding-inline: 0;
      }
    }
  }
</style>

<style lang="scss">
  // overrides

  .ce-block__content {
    max-width: none;
  }

  .ce-toolbar__content {
    max-width: none;
  }

  .cdx-notifies {
    bottom: 5rem !important;
  }
</style>
